import clone from 'clone'
import { ID, OverrideSchedule, Port, Strategy } from '../../types/models'
import { QuestionMark } from '@mui/icons-material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { SvgIconTypeMap } from '@mui/material'
import { AppContextProps } from '../../hooks/useApp'
import { PortControl } from '../ports/PortControl'
import { PortConfig, PortType, TemperatureDriver, EnergyStorageFCRDriver, PortConfigEnergyStorageParams, UncommittedConfig } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { SystemControl } from '../SystemControl'
import { Widget, WidgetData } from '../widgets/Widget'
import { callAxios } from '../../hooks/useAxios'
import { HeatPumpCalibration } from './heat-pump-defaults'

export interface ApplianceConfigBattery {
  comments?: string
  has_han?: boolean
  has_solar?: boolean
  strategy?: Strategy
  inverter?: string
  battery?: string
  capacity?: number
  power?: number
  solar_connected?: boolean
  solar_connected_power?: number
  solar_onprem?: boolean
  solar_onprem_power?: number
}

export type ApplianceConfigFCRBattery = ApplianceConfigBattery & {
  enabled: boolean,
  driver: EnergyStorageFCRDriver,
  params: PortConfigEnergyStorageParams,
  inverter_min_up_power: number | null
  inverter_max_up_power: number | null
  inverter_min_down_power: number | null
  inverter_max_down_power: number | null
  /** Reserve participation tuning: multiplier on every bid price of the unit (unset = 1.00). */
  bid_price_factor?: number
}

export interface ApplianceConfigEvCharger {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  override?: OverrideSchedule
  allowed_hours?: [number, number]
  charging_hours?: number
}

export interface ApplianceConfigRadiator {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
}

export type DutyCycleMethod = 'FIXED' | 'WEATHER' | 'CLOCK'

export interface ApplianceConfigAirAirHeatPump {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
}

export interface ApplianceConfigAirWaterHeatPump {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  domestice_water?: boolean
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
}

export interface ApplianceConfigGroundSourceHeatPump {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  domestice_water?: boolean
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
}

/**
 * Modbus heat pump on port 0x23 (pilot). saving_or_heat shifts the comfort
 * setpoint, heat_preservation is the time constant, calibration (installer)
 * the heat demand model; the port settings live on the 0x23 port conf.
 */
export interface ApplianceConfigHeatPump {
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  use_temperature?: boolean
  temperature_port_id?: number
  calibration?: Partial<HeatPumpCalibration>
  override?: OverrideSchedule
}

export interface ApplianceConfigBoiler {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  volume?: number
  people?: number
  override?: OverrideSchedule
}

export interface ApplianceConfigInFloorHeating {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
  summer_heating?: boolean
}

export interface ApplianceConfigStorageTank {
  advanced?: boolean
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  saving_or_heat?: number
  heat_preservation?: number
  override?: OverrideSchedule
  use_temperature?: boolean
  temperature_port_id?: number
  temperature?: number
  opt_by_method?: DutyCycleMethod
}

export interface ApplianceConfigUnknownRelay {
  strategy?: Strategy
  plan_by_price?: boolean
  pv_priority: number
  override?: OverrideSchedule
}

export interface ApplianceConfigEnergyMeter {
}

export interface ApplianceConfigSolar {
}

export interface ApplianceConfigTemperature {
}

export interface ApplianceConfigPrice {
}

export interface ApplianceConfigEnergyHistory {
  steps: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
}

export interface ApplianceConfigPriceGraph {
}

export type ApplianceConfigRelay =
  ApplianceConfigEvCharger |
  ApplianceConfigRadiator |
  ApplianceConfigAirAirHeatPump |
  ApplianceConfigAirWaterHeatPump |
  ApplianceConfigGroundSourceHeatPump |
  ApplianceConfigHeatPump |
  ApplianceConfigBoiler |
  ApplianceConfigInFloorHeating |
  ApplianceConfigStorageTank |
  ApplianceConfigUnknownRelay

export type ApplianceConfig =
  ApplianceConfigEnergyMeter |
  ApplianceConfigSolar |
  ApplianceConfigTemperature |
  ApplianceConfigPrice |
  ApplianceConfigEnergyHistory |
  ApplianceConfigPriceGraph |
  ApplianceConfigRelay

export type ApplianceType =
  'BATTERY' |
  'EV_CHARGER' |
  'RADIATOR' |
  'AIR_AIR_HEAT_PUMP' |
  'AIR_WATER_HEAT_PUMP' |
  'GROUND_SOURCE_HEAT_PUMP' |
  'BOILER' |
  'IN_FLOOR_HEATING' |
  'STORAGE_TANK' |
  'ENERGY_METER' |
  'SOLAR_PANEL' |
  'TEMPERATURE' |
  'OTHER_RELAY' |
  'FCR_BATTERY' |
  'HEAT_PUMP'

export type ApplianceSubtype = PortType | TemperatureDriver

export interface ApplianceData {
  id?: ID
  location_id: ID
  name: string
  type: ApplianceType
  ports: ID[]
  config: ApplianceConfig
  widgets?: WidgetData[]
}

export interface DashboardViewProps {
  refresh: () => void
}

export interface ConfigurationViewProps {
  onClickVisibility: () => Promise<void>
  onClickEdit: () => void
  onChangeName: (e: string) => void
  onSave: () => Promise<void>
  edit?: boolean
}

/**
 * Thrown by save-flow guards to abort a save whose error has already been
 * shown to the user (and reported to Slack where relevant). Any other
 * exception escaping a save handler is unexpected and must be surfaced.
 */
export class SaveAbortedError extends Error {}

/**
 * A description of an appliance connected to some gadget.
 */
export class Appliance<DataType={}, ConfigType extends ApplianceConfig=ApplianceConfig> {
  id?: ID
  name: string
  type: ApplianceType
  location_id: ID
  portIDs: ID[]
  config: ConfigType

  data?: DataType
  control: SystemControl
  controls: PortControl<Port, PortConfig>[]

  app: AppContextProps

  constructor(data: ApplianceData, control: SystemControl, app: AppContextProps) {
    this.id = data.id
    this.name = data.name
    this.type = data.type
    this.portIDs = clone(data.ports)
    this.config = clone(data.config as ConfigType)
    this.location_id = data.location_id
    this.control = control
    this.controls = []
    this.app = app
  }

  toJSON(): Record<string, unknown> {
    const ret = {
      name: this.name,
      type: this.type,
      ports: clone(this.portIDs),
      config: clone(this.config)
    }
    return this.id ? { id: this.id, ...ret } : ret
  }

  /**
   * A mapping of URLs that we want for data to be fetched.
   */
  dataUrls(): Record<string, string> {
    return {}
  }

  /**
   * Slots needed in the 4-column grid.
   */
  get width(): number {
    return 1
  }

  /**
   * Multiples of base row needed.
   */
  get height(): number {
    return 1
  }

  /**
   * Visualization hints.
   */
  // get textColor(): string {
  //   return 'black'
  // }

  // get bgColor(): string {
  //   return 'white'
  // }

  get Icon(): OverridableComponent<SvgIconTypeMap<{}, 'svg'>> {
    return QuestionMark
  }

  // get borderRadius(): string {
  //   return '0.5rem'
  // }

  /**
   * Check if the appliance uses device.
   */
  isGeneric(): boolean {
    return this.portIDs.length === 0
  }

  /**
   * Determine appliance subtype.
   */
  getSubtype(): ApplianceSubtype {
    return this.getControl(0).getSubtype()
  }

  /**
   * Check if the given port is suitable for this application.
   */
  isSuitable(ctrl: PortControl<Port, PortConfig>, subtype: ApplianceSubtype): boolean {
    return ctrl.port.type === subtype
  }

  /**
   * If true, this appliance has both advanced and simple configurator.
   */
  hasAdvanced() {
    return false
  }

  /**
   * If true, this appliance has test page.
   */
  hasTestPage() {
    return true
  }

  /**
   * If true, go to the test page even when configuring old appliance.
   */
  alwaysTestPage() {
    return false
  }

  /**
   * Check if the configuration is ready for continuing appliance creation.
   */
  canContinue() {
    return true
  }

  /**
   * Collect and translate all errors in ports.
   */
  errorFlags(): string[] {
    return this.controls
      .map(ctrl => ctrl.port.flags)
      .reduce((prev, cur) => prev.concat(cur))
      .map(flag => this.app.t(`Error-${flag}`))
  }

  /**
   * Set up controls for ports.
   */
  connectControls(control: SystemControl): void {
    this.controls = []
    for (const id of this.portIDs) {
      const portCtrl = control.portControlForId(id)
      this.controls.push(portCtrl)
    }
  }

  /**
   * Get the indexed control or throw an error if not initialized or found.
   */
  getControl<T extends PortControl<Port, PortConfig>>(n: number): T {
    if (this.controls[n] === undefined) {
      throw new Error(`Cannot access port control [${n}], since it is not loaded (call connectControls()?).`)
    }
    return this.controls[n] as T
  }

  /**
   * Find ports matching the filter.
   */
  findControslByFilter(fn: (ctrl: PortControl<Port, PortConfig>) => boolean): PortControl<Port, PortConfig>[] {
    const match = this.controls.filter(ctrl => fn(ctrl))
    return match
  }

  /**
   * Verify that we have exactly one relay port and return it.
   */
  findOneControl<T extends PortControl<Port, PortConfig>>(type: PortType, alt: PortType | undefined = undefined): T {
    let match = this.findControslByFilter(ctrl => ctrl.port.type === type)
    if (match.length === 0 && alt !== undefined) {
      match = this.findControslByFilter(ctrl => ctrl.port.type === alt)
    }

    if (match.length === 0) {
      throw new Error(`Cannot find any ${type} ports from the list of controls.`)
    }
    if (match.length > 1) {
      throw new Error(`Found too many ${type} ports from the list of controls.`)
    }
    return match[0] as T
  }

  /**
   * Get the given name or just the type if not given.
   */
  getName(): string {
    return this.name || this.getType()
  }

  /**
   * Get the translated type name.
   */
  getType(): string {
    return this.app.t(`Appliance-${this.type}`)
  }

  /**
   * List of sub-selections available for this type of appliance as mapping from translations to codes.
   */
  getSubtypes(): Record<string, string> {
    return {}
  }

  /**
   * Get the port type to select by default, when creating appliances.
   */
  getDefaultPortType(): PortType {
    throw new Error(`Appliance ${this.type} does not implement default port type.`)
  }

  /**
   * Get initial values for configuration.
   */
  getDefaultConfig(): ApplianceConfig {
    return {}
  }

  /**
   * Get default widgets for appliance. The first one is primary widget for editing purposes.
   */
  getDefaultWidgets(): Widget[] {
    return []
  }

  // TODO: All these drawing functions should be named as drawXxxx().

  /**
   * Get the confugration presentation for the top part of the screen.
   */
  configTop(appliances: Appliance[]): React.ReactNode {
    return <></>
  }

  /**
   * Full width informative widget on the top of configuration panel. By default use first port's.
   */
  infoTop() : React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].infoTop() : undefined
  }

  /**
   * Informative widget for the left side of configuration panel. By default use first port's.
   */
  infoLeft(): React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].infoLeft() : undefined
  }

  /**
   * Informative widget for the right side of configuration panel. By default use first port's.
   */
  infoRight(): React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].infoRight() : undefined
  }

  /**
   * Non-flashable configuration panel on the left. By default use first port's.
   */
  configTopLeft() : React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].configTopLeft() : undefined
  }

  /**
   * Non-flashable configuration panel on the right. By default use first port's.
   */
  configTopRight() : React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].configTopRight() : undefined
  }

  /**
   * Flashable configuration panel on the left. By default use first port's.
   */
  configBottomLeft() : React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].configBottomLeft() : undefined
  }

  /**
   * Flashable configuration panel on the right. By default use first port's.
   */
  configBottomRight() : React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].configBottomRight() : undefined
  }

  /**
   * Get the confugration presentation for the bottom part of the screen.
   */
  configBottom(appliances: Appliance[]): React.ReactNode | undefined {
    return this.controls.length ? this.controls[0].configBottom() : undefined
  }

  /**
   * Draw custom part of the front page widget.
   */
  widget(manual: boolean, overriding: boolean, onRefresh: () => void): React.ReactNode {
    return <></>
  }

  /**
   * Save temporary editing copy to the local store.
   */
  static storeConfig(config: ApplianceConfig): void {
    localStorage.setItem('temp-appliance-config', JSON.stringify(config))
  }

  /**
   * Restore temporary editing copy from the local store.
   */
  static restoreConfig(): ApplianceConfig {
    const conf = localStorage.getItem('temp-appliance-config')
    return conf ? JSON.parse(conf) : {}
  }

  /**
   * Remove stored config.
   */
  static clearConfig(): void {
    localStorage.removeItem('temp-appliance-config')
  }

  /**
   * Change the config and refresh screen by default.
   */
  setConfig(key: string, value: unknown, refresh: boolean = true): void {
    (this.config as Record<string, unknown>)[key] = value
    Appliance.storeConfig(this.config)
    if (refresh) this.control.refresh()
  }

  /**
   * Retrieve the configuration value.
   */
  getConfig<T = any>(key: string): T {
    return (this.config as Record<string, unknown>)[key] as T
  }

  /**
   * Hook to sync any dual-state config before saving to backend.
   * Called before POST (creation) and before PATCH (editing).
   */
  prepareSave(): void {}

  /**
   * Text of an extra confirmation the user must accept before the save runs,
   * or null when the save needs no confirmation. Used for changes with
   * consequences outside this device, e.g. leaving the reserve market.
   */
  saveConfirmation(): string | null {
    return null
  }

  /**
   * Hook run after the appliance has been saved but before its port configs
   * are flashed. Use it for anything that must be true *before* the device
   * changes behaviour; throw `SaveAbortedError` to call the whole save off
   * with nothing flashed.
   */
  async beforeFlash(): Promise<void> {}

  /**
   * Hook run once the appliance, its widgets and its port configs have all
   * been saved and flashed. Failures here must not lose the save that already
   * happened, so implementations report their own errors.
   */
  async afterSave(): Promise<void> {}

  /**
   * Write configuration to the backend.
   * Does NOT push config to port controls (applyConfig) — callers that need to flash
   * should use buildFlashPayload() + SystemControl.flashItems() instead.
   */
  async saveConfig(): Promise<void> {
    const res = await callAxios<ApplianceData>({
      method: 'PATCH',
      url: `/appliances/${this.id}`,
    }, this.toJSON())

    if (!res.success) {
      this.app.addError(res)
    }
  }

  /**
   * Hook executed when entering configuration screen.
   */
  initializeConfig(portControl: PortControl<Port, PortConfig>, type: ApplianceType, subtype: ApplianceSubtype) {
  }

  /**
   * Port types that must never be flashed from the UI. The server attaches these
   * to battery appliances (Appliance.attach_batopt2_ports/0) for the
   * BatteryStateWidget, but batopt2 (0x222) conf is firmware-owned and
   * soc_plan (0x223) has no config packet at all — including either one makes
   * the server reject the whole flash list with 400 BAD_PARAMS.
   */
  static readonly NON_FLASHABLE_PORT_TYPES: PortType[] = ['BATTERY_OPTIMIZER2', 'BATTERY_OPTIMIZER_SOC_PLAN']

  /**
   * If true, saving an existing appliance flashes only the ports whose config
   * the device is not known to have already (see PortControl.isConfirmedOnDevice).
   *
   * Every flash ends in a confmit, which reboots the device. For an appliance
   * whose "Save" is really the way to its live-data page (EM_3PH: calibration,
   * currents) the reboot on every visit is harmful, so it opts in here. Creating
   * an appliance always flashes, whatever this returns.
   */
  flashOnlyWhenNeeded(): boolean {
    return false
  }

  /**
   * Build the flash payload for this appliance's ports.
   * Returns items ready to be passed to SystemControl.flashItems().
   *
   * Default: flash all connected port controls except non-flashable types.
   * Subclasses with special logic (RelayAppliance, FCRBatteryAppliance) override this.
   */
  buildFlashPayload(): UncommittedConfig[] {
    return this.controls
      .filter(ctrl => !Appliance.NON_FLASHABLE_PORT_TYPES.includes(ctrl.port.type))
      .map(ctrl => DeviceControl.buildPortFlashItem(ctrl))
  }

  /**
   * Push appliance configuration converted to the appropriate port configurations via port controller.
   */
  applyConfig(): void {
  }

  /**
   * A hook called when appliance is created and ports and widgets ready.
   */
  createHook(): void {
  }

  /**
   * Hook to fine tune the list of ports available for selection for appliance.
   */
  filterSuitablePorts(ports: PortControl<Port, PortConfig>[]): PortControl<Port, PortConfig>[] {
    return ports
  }

  /**
   * Implementation of `filterSuitablePorts()` that automatically takes one per device.
   * @param ports
   * @returns
   */
  autoAllocate(ports: PortControl<Port, PortConfig>[]): PortControl<Port, PortConfig>[] {
    const suitable: PortControl<Port, PortConfig>[] = []
    const seen = new Set<ID>()
    ports.forEach(ctrl => {
      if (!seen.has(ctrl.port.device_id as ID)) {
        seen.add(ctrl.port.device_id as ID)
        suitable.push(ctrl)
      }
    })
    return suitable
  }

  /**
   * Find the locations that need their own netsum appliances and add missing and delete obsolete.
   * Reconfigures multi-opt roles and returns flash items for all ports changed (role changes + netsum enables).
   * @param control
   * @returns Flash items for ports that were changed by this method.
   */
  static async configureMultiOpt(control: SystemControl): Promise<UncommittedConfig[]> {
    const items = control.configureMultiOptRoles()
    const netsums = control.netsumNeeded()

    for (const locationId of Object.keys(netsums)) {

      if (!netsums[locationId as unknown as ID]) {
        continue
      }

      const netSumAppDeviceIds = new Set(
        (await control.appliances(locationId as unknown as ID, 'ENERGY_METER', 'EM_NETSUM'))
          .map(app => app.getControl(0).device.id)
      )

      if (netSumAppDeviceIds.has(netsums[locationId as unknown as ID]?.device.id)) {
        continue
      }

      // TODO: We are repeating a lot here. Must go to the one place. Create automatically default widgets too.
      const netSumPort = netsums[locationId as unknown as ID]?.findPortByType('EM_NETSUM')
      if (netSumPort) {
        netSumPort.setConfig('enabled', true)
        items.push(DeviceControl.buildPortFlashItem(netSumPort))
      }

      const netSumData: ApplianceData = {
        location_id: locationId as unknown as ID,
        name: control.app.t('PortType-Netsum Power Meter'),
        type: 'ENERGY_METER',
        ports: [netSumPort?.port.id as ID],
        config: {}
      }
      const netSumApp = control.makeAppliance(netSumData)

      const res = await callAxios<ApplianceData>({
        method: 'POST',
        url: '/appliances',
      }, netSumData)

      if (res.success) {
        for (const widget of netSumApp.getDefaultWidgets()) {
          const widgetData: WidgetData = { ...widget.toJSON(), appliances: [res.data.id] as ID[], location_id: netSumApp.location_id } as WidgetData
          const res2 = await callAxios<WidgetData>({
            method: 'POST',
            url: '/widgets',
          }, widgetData)
          !res2.success && control.app.addError(res2)
        }
      } else {
        control.app.addError(res)
      }
    }

    return items
  }
}
